import type { PartnerActivityEntry } from "../model";
import { activityColumn, type ActivitySort, type ActivitySortColumn } from "./activityColumns";
import type { ActivityFilters } from "./partnerDeepDive.types";

/**
 * Pure view transforms for the per-partner Recent Activity table — the same
 * `(entries, filters) -> entries` / `(entries, sort) -> entries` shape as
 * `metricsView.ts`'s `applyFilters` / `sortRows`, scoped to one partner's
 * `PartnerActivityEntry[]` instead of the portfolio's `PartnerMetricsRow[]`.
 *
 * TODO(PM-FR-007): the POC's real activity table has a per-row "Download"
 * link exporting `errorDetail: { httpStatus, message }` as JSON for Error
 * rows. Deliberately not ported — `PartnerActivityEntry` doesn't carry
 * `errorDetail` (CPD-69 made the same call for the same reason: activity
 * quality disclosure / error detail is PM-FR-007, not committed scope yet
 * per the PRD's own Open Question #3). Add it here if PM-FR-007 gets
 * scoped into a ticket.
 */

export const DEFAULT_ACTIVITY_FILTERS: ActivityFilters = {
  type: "all",
  region: "all",
  status: "all",
};

/** True when every filter is at its default ("all") — drives the activity filter bar's Reset button. */
export function isDefaultActivityFilters(filters: ActivityFilters): boolean {
  return (Object.keys(DEFAULT_ACTIVITY_FILTERS) as (keyof ActivityFilters)[]).every(
    (key) => filters[key] === DEFAULT_ACTIVITY_FILTERS[key]
  );
}

/** Default sort — newest first, matching the POC's `activitySort = {col:'time', dir:'desc'}`. */
export const DEFAULT_ACTIVITY_SORT: ActivitySort = { column: "time", direction: "desc" };

export function applyActivityFilters(
  entries: PartnerActivityEntry[],
  filters: ActivityFilters
): PartnerActivityEntry[] {
  return entries.filter((entry) => {
    if (filters.type !== "all" && entry.type !== filters.type) return false;
    if (filters.region !== "all" && entry.region !== filters.region) return false;
    if (filters.status !== "all" && entry.status !== filters.status) return false;
    return true;
  });
}

/** Non-mutating sort — returns a new array. */
export function sortActivity(
  entries: PartnerActivityEntry[],
  sort: ActivitySort
): PartnerActivityEntry[] {
  const factor = sort.direction === "asc" ? 1 : -1;
  const { sortValue } = activityColumn(sort.column);
  return [...entries].sort((a, b) => {
    const av = sortValue(a);
    const bv = sortValue(b);
    // Missing values stay last in either direction; zero is a real result count.
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === "number" && typeof bv === "number") return factor * (av - bv);
    return factor * String(av).localeCompare(String(bv));
  });
}

/** Toggle direction when re-selecting the current column; otherwise use its default. */
export function nextActivitySort(current: ActivitySort, column: ActivitySortColumn): ActivitySort {
  if (current.column === column) {
    return { column, direction: current.direction === "asc" ? "desc" : "asc" };
  }
  return { column, direction: activityColumn(column).sortAscByDefault ? "asc" : "desc" };
}

/**
 * Mean result count across successful (`Hit`) entries only, matching
 * Metrics insight's "results per successful execution" reading of
 * PM-FR-003/004 — an `Error` row is excluded rather than averaged in as a 0.
 * Unscoped by API type, unlike Metrics insight signals: this powers the activity
 * table's own summary line ("Avg. results per search/query shown below"),
 * which sums whatever the table is currently filtered to, any type mixed in.
 */
export function averageResultCount(entries: PartnerActivityEntry[]): number | null {
  const resultCounts = entries
    .filter((entry) => entry.status === "Hit")
    .map((entry) => entry.resultCount)
    .filter((count): count is number => count !== null);
  return resultCounts.length
    ? Math.round(resultCounts.reduce((a, b) => a + b, 0) / resultCounts.length)
    : null;
}
